import { computed, ref, type ComputedRef, type Ref } from 'vue'
import * as THREE from 'three'
import { v4 as uuidv4 } from 'uuid'
import type { SceneManager } from '@/three/SceneManager'
import type { EditorTool, Point3D, SceneObject } from '@/types/scene'
import type { useSceneStore, useSelectionStore } from '@/stores/scene'
import type { HistoryManager } from '@/three/HistoryManager'
import { getClickWorldPosition } from '@/three/DragHandler'
import { getPipePresetById } from '@/components-lib/pipePresets'
import { buildOrthogonalPipeRoute } from '@/utils/pipeRoute'
import { findNearestPipePortByXZ, type PipePortWorld } from '@/three/pipePorts'

type SceneStore = ReturnType<typeof useSceneStore>
type SelectionStore = ReturnType<typeof useSelectionStore>

interface PipeConnectToolDeps {
  getSm: () => SceneManager | undefined
  sceneStore: SceneStore
  selectionStore: SelectionStore
  histManager: HistoryManager
  interactionReadonly: ComputedRef<boolean>
  currentFloorId: Ref<string>
  getEditorTool: () => EditorTool
  getPipePresetId: () => string
}

function getGroundY(deps: PipeConnectToolDeps) {
  const floor = deps.sceneStore.activeFloors().find(f => f.id === deps.currentFloorId.value)
  return floor?.elevation ?? 0
}

export function createPipeConnectTool(deps: PipeConnectToolDeps) {
  const root = new THREE.Group()
  root.name = '__pipeConnect__'
  const startPort = ref<PipePortWorld | null>(null)
  const hoverPort = ref<PipePortWorld | null>(null)
  const hoverCursor = ref<Point3D | null>(null)

  function ensureRoot() {
    const sm = deps.getSm()
    if (!sm) return
    if (!sm.scene.getObjectByName(root.name)) sm.scene.add(root)
  }

  function clearRoot() {
    while (root.children.length) {
      const c = root.children.pop()
      if (!c) break
      if (c instanceof THREE.Mesh || c instanceof THREE.Line) {
        c.geometry.dispose()
        const m = c.material
        if (Array.isArray(m)) m.forEach(mm => mm.dispose())
        else m.dispose()
      }
    }
  }

  function drawMarker(pos: THREE.Vector3, color: number) {
    const ring = new THREE.Mesh(
      new THREE.RingGeometry(0.6, 0.95, 22),
      new THREE.MeshBasicMaterial({ color, side: THREE.DoubleSide, transparent: true, opacity: 0.9 }),
    )
    ring.rotation.x = -Math.PI / 2
    ring.position.set(pos.x, pos.y + 0.03, pos.z)
    root.add(ring)
  }

  function drawPreviewLine(points: Point3D[]) {
    if (points.length < 2) return
    const geo = new THREE.BufferGeometry().setFromPoints(points.map((p) => new THREE.Vector3(p.x, p.y, p.z)))
    const line = new THREE.Line(
      geo,
      new THREE.LineDashedMaterial({ color: '#38bdf8', dashSize: 0.7, gapSize: 0.35, transparent: true, opacity: 0.92 }),
    )
    line.computeLineDistances()
    root.add(line)
  }

  function syncVisuals() {
    ensureRoot()
    clearRoot()
    if (startPort.value) drawMarker(startPort.value.position, 0x22c55e)
    if (hoverPort.value) drawMarker(hoverPort.value.position, 0x38bdf8)
    if (startPort.value) {
      const routeTarget = hoverPort.value
        ? { x: hoverPort.value.position.x, y: hoverPort.value.position.y, z: hoverPort.value.position.z }
        : hoverCursor.value
      if (routeTarget) {
        const route = buildOrthogonalPipeRoute(
          {
            position: { x: startPort.value.position.x, y: startPort.value.position.y, z: startPort.value.position.z },
          },
          { position: routeTarget },
          { gridSize: deps.sceneStore.activeScene()?.scene?.gridSize ?? 1, forceOrthogonal: true },
        )
        drawPreviewLine(route.waypoints)
      }
    }
    deps.getSm()?.markDirty()
  }

  function clearDraft() {
    startPort.value = null
    hoverPort.value = null
    hoverCursor.value = null
    syncVisuals()
  }

  function onPointerMove(ev: MouseEvent) {
    if (deps.getEditorTool() !== 'pipeConnect') return
    const sm = deps.getSm()
    if (!sm) return
    const gridSize = deps.sceneStore.activeScene()?.scene?.gridSize ?? 1
    const point = getClickWorldPosition(ev, sm.renderer.domElement, sm.camera, { gridSize, groundY: getGroundY(deps) })
    if (!point) return
    hoverCursor.value = point
    hoverPort.value = findNearestPipePortByXZ(point.x, point.z, deps.sceneStore.activeObjects(), { threshold: 3.2 })
    syncVisuals()
  }

  function createPipeByPorts(start: PipePortWorld, end: PipePortWorld) {
    const gridSize = deps.sceneStore.activeScene()?.scene?.gridSize ?? 1
    const preset = getPipePresetById(deps.getPipePresetId())
    const route = buildOrthogonalPipeRoute(
      { objectId: start.objectId, portId: start.portId, position: { x: start.position.x, y: start.position.y, z: start.position.z } },
      { objectId: end.objectId, portId: end.portId, position: { x: end.position.x, y: end.position.y, z: end.position.z } },
      { gridSize, forceOrthogonal: true, minSegmentLength: 0.01 },
    )
    if (route.waypoints.length < 2) return
    const anchor = route.waypoints[0]!
    const localWaypoints = route.waypoints.map((w) => ({ x: w.x - anchor.x, y: w.y - anchor.y, z: w.z - anchor.z }))
    const obj: SceneObject = {
      id: uuidv4(),
      type: 'pipe_polyline',
      name: '折线管线',
      position: [anchor.x, anchor.y, anchor.z],
      rotationY: 0,
      params: {
        waypoints: localWaypoints,
        waypointSpace: 'local',
        closed: false,
        diameter: preset.diameter,
        color: preset.color,
        flowEnabled: true,
        flowPreview: true,
        flowSpeed: 1.8,
        flowOpacity: 0.72,
        flowColor: '#00e5ff',
        autoFittings: [],
        endpoints: {
          start: { objectId: start.objectId, portId: start.portId },
          end: { objectId: end.objectId, portId: end.portId },
        },
      },
      label: { text: '折线管线', visible: false },
      floorId: deps.currentFloorId.value,
    }
    deps.histManager.push(deps.sceneStore.getEditorSnapshot())
    deps.sceneStore.addObject(obj)
    deps.selectionStore.select(obj.id, { x: 0, y: 0 })
  }

  function onCanvasClick(ev: MouseEvent) {
    if (deps.interactionReadonly.value || deps.getEditorTool() !== 'pipeConnect') return false
    const sm = deps.getSm()
    if (!sm) return false
    const gridSize = deps.sceneStore.activeScene()?.scene?.gridSize ?? 1
    const point = getClickWorldPosition(ev, sm.renderer.domElement, sm.camera, { gridSize, groundY: getGroundY(deps) })
    if (!point) return false
    const port = findNearestPipePortByXZ(point.x, point.z, deps.sceneStore.activeObjects(), { threshold: 3.2 })
    if (!port) return false
    if (!startPort.value) {
      startPort.value = port
      hoverPort.value = port
      syncVisuals()
      return true
    }
    if (startPort.value.objectId === port.objectId && startPort.value.portId === port.portId) return true
    createPipeByPorts(startPort.value, port)
    clearDraft()
    return true
  }

  function dispose() {
    clearRoot()
    root.parent?.remove(root)
  }

  const hasDraft = computed(() => !!startPort.value)
  return {
    onPointerMove,
    onCanvasClick,
    clearDraft,
    syncVisuals,
    dispose,
    hasDraft,
  }
}
